import React, { useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import {
  Divider,
  TextField,
  Stack,
  Typography,
  Box,
  Button,
} from '@mui/material';
import DialogContent from '@mui/material/DialogContent';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { Camera } from 'iconsax-react';
import { useTheme } from '@mui/material/styles';
import ApiClient from '../../../config/api';
import { removeNonNumeric } from '../../../components/converter/index';
import requestNativeCameraPermission from '../../../components/getCameraPermission';

const getJpegBase64 = (sourceCanvas, maxWidth = 1280, quality = 0.7) => {
  let width = sourceCanvas.width;
  let height = sourceCanvas.height;

  if (!width || !height) {
    return null;
  }

  if (width > maxWidth) {
    height = Math.round((height * maxWidth) / width);
    width = maxWidth;
  }

  const output = document.createElement('canvas');
  output.width = width;
  output.height = height;
  output.getContext('2d').drawImage(sourceCanvas, 0, 0, width, height);

  const dataUrl = output.toDataURL('image/jpeg', quality);
  return {
    preview: dataUrl,
    base64: dataUrl.split(',')[1] || '',
  };
};

const VerifyIdentification = ({ open, onClose, onSuccess, activityId }) => {
  const theme = useTheme();
  const api = ApiClient();
  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const [capturedImage, setCapturedImage] = useState(null);
  const [nationalImage, setNationalImage] = useState('');
  const [nationalId, setNationalId] = useState('');
  const [openCamera, setOpenCamera] = useState(false);
  const [hasCameraAccess, setHasCameraAccess] = useState(false);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const stopCamera = () => {
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const markCameraReady = () => {
    const video = videoRef.current;
    if (video?.videoWidth > 0 && video?.videoHeight > 0) {
      setIsCameraReady(true);
    }
  };

  const attachStreamToVideo = (mediaStream) => {
    if (videoRef.current && mediaStream) {
      videoRef.current.srcObject = mediaStream;
      if (videoRef.current.readyState >= 2) {
        markCameraReady();
      }
    }
  };

  const requestCameraAccessWeb = async () => {
    try {
      setHasCameraAccess(false);
      setIsCameraReady(false);
      stopCamera();
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });

      streamRef.current = mediaStream;
      setHasCameraAccess(true);
      attachStreamToVideo(mediaStream);
    } catch (cameraError) {
      console.error('Error accessing camera:', cameraError);
      setHasCameraAccess(false);
      setIsCameraReady(false);
      setOpenCamera(false);
      setError('دسترسی به دوربین امکان‌پذیر نیست');
    }
  };

  const startCamera = async () => {
    setError('');
    setCapturedImage(null);
    setNationalImage('');
    setHasCameraAccess(false);
    setIsCameraReady(false);
    setOpenCamera(true);

    const nativeGranted = await requestNativeCameraPermission();
    if (nativeGranted === false) {
      setOpenCamera(false);
      setError('برای ثبت تصویر، دسترسی دوربین را تایید کنید');
      return;
    }

    requestCameraAccessWeb();
  };

  const captureImage = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const videoWidth = video?.videoWidth || 0;
    const videoHeight = video?.videoHeight || 0;

    if (!hasCameraAccess || !video || !canvas || !videoWidth || !videoHeight) {
      setError('دسترسی به دوربین امکان‌پذیر نیست');
      return;
    }

    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }

    canvas.width = videoWidth;
    canvas.height = videoHeight;
    context.drawImage(video, 0, 0, videoWidth, videoHeight);

    const image = getJpegBase64(canvas);
    if (!image) {
      setError('ثبت تصویر امکان‌پذیر نیست');
      return;
    }

    setCapturedImage(image.preview);
    setNationalImage(image.base64);
    stopCamera();
    setIsCameraReady(false);
  };

  useEffect(() => {
    if (open) {
      setCapturedImage(null);
      setNationalImage('');
      setNationalId('');
      setError('');
      setIsLoading(false);
      setHasCameraAccess(false);
      setIsCameraReady(false);
      startCamera();
    } else {
      stopCamera();
      setOpenCamera(false);
      setHasCameraAccess(false);
      setIsCameraReady(false);
    }

    return () => stopCamera();
  }, [open]);

  useEffect(() => {
    if (openCamera && !capturedImage) {
      attachStreamToVideo(streamRef.current);
    }
  }, [openCamera, capturedImage]);

  const handleNationalIdChange = (event) => {
    const nextValue = removeNonNumeric(event.target.value)?.slice(0, 10) || '';
    setNationalId(nextValue);
    setError('');
  };

  const handleSubmit = async () => {
    if (!nationalImage) {
      setError('لطفا تصویر کارت ملی را ثبت کنید');
      return;
    }

    if (!nationalId || nationalId.length !== 10) {
      setError('لطفا کد ملی ۱۰ رقمی را وارد کنید');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      await api.post(
        `${process.env.REACT_APP_SHIPITO_API_URL}/api/app/v1/shipito/tasks/verify-by-identification`,
        {
        activityId,
        nationalId,
        nationalImage,
      });
      stopCamera();
      onSuccess?.();
    } catch (submitError) {
      setError(
        submitError?.response?.data?.message ||
          submitError?.response?.message ||
          'احراز هویت انجام نشد'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      onClose={onClose}
      aria-labelledby="verify-identification-title"
      open={Boolean(open)}
      maxWidth="sm"
      fullWidth={true}
      fullScreen={true}
      style={{ zIndex: 99999 }}
    >
      <Stack
        sx={{
          position: 'sticky',
          pt: 2,
          top: 0,
          zIndex: 1,
          backgroundColor: 'background.paper',
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}
      >
        <Typography
          id="verify-identification-title"
          fontSize={20}
          fontWeight={600}
          textAlign={'right'}
        >
          احراز هویت
        </Typography>
        <KeyboardBackspaceIcon
          className={'clickable'}
          sx={{
            transform: 'rotate(180deg)',
            cursor: 'pointer',
            marginRight: '8px',
            marginLeft: '8px',
            fontSize: '24px',
            minWidth: 44,
            minHeight: 44,
          }}
          onClick={onClose}
        />
      </Stack>
      <Divider
        sx={{
          width: '100%',
          marginTop: '10px',
        }}
      />

      <DialogContent sx={{ pt: '0px', mt: 2 }}>
        <Typography variant="subtitle1" textAlign="right" fontWeight={600} mb={1}>
          تصویر کارت ملی
        </Typography>
        <Box
          sx={{
            minHeight: '240px',
            cursor: 'pointer',
            borderRadius: '16px',
            bgcolor: 'background.card',
            border: '1px solid',
            borderColor: 'divider',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex',
            p: 2,
          }}
          onClick={() => {
            if (!openCamera) {
              startCamera();
            }
          }}
        >
          <canvas ref={canvasRef} style={{ display: 'none' }} />
          {!openCamera ? (
            <>
              <Camera size="48" color={theme.palette.text.primary} />
              <Typography textAlign={'center'} variant="subtitle1">
                باز کردن دوربین
              </Typography>
            </>
          ) : capturedImage ? (
            <img
              src={capturedImage}
              alt="تصویر کارت ملی"
              style={{
                width: '100%',
                maxWidth: '400px',
                maxHeight: '300px',
                objectFit: 'cover',
                borderRadius: '8px',
                border: '1px solid #D4D4D8',
              }}
            />
          ) : (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              onLoadedMetadata={markCameraReady}
              onCanPlay={markCameraReady}
              onPlaying={markCameraReady}
              style={{
                width: '100%',
                maxWidth: '400px',
                borderRadius: '8px',
                border: '1px solid #D4D4D8',
                background: 'black',
              }}
            />
          )}
        </Box>

        {capturedImage ? (
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            disabled={isLoading}
            onClick={startCamera}
            sx={{ mt: 2, minHeight: 48 }}
          >
            گرفتن مجدد
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            fullWidth
            disabled={!hasCameraAccess || !isCameraReady || isLoading}
            onClick={captureImage}
            sx={{
              mt: 2,
              minHeight: 48,
              color: 'primary.contrastText',
            }}
          >
            گرفتن
          </Button>
        )}

        <TextField
          label="کد ملی"
          placeholder="کد ملی ۱۰ رقمی را وارد کنید"
          sx={{ width: '100%', mt: 3 }}
          value={nationalId}
          onChange={handleNationalIdChange}
          disabled={isLoading}
          inputProps={{
            inputMode: 'numeric',
            maxLength: 10,
            style: { textAlign: 'right' },
            'aria-label': 'کد ملی',
          }}
        />

        {error ? (
          <Typography color="error" textAlign="center" fontSize={13} mt={2}>
            {error}
          </Typography>
        ) : null}

        <Button
          variant="contained"
          color="primary"
          fullWidth
          disabled={isLoading || !nationalImage || nationalId.length !== 10}
          onClick={handleSubmit}
          sx={{
            mt: 3,
            minHeight: 52,
            color: 'primary.contrastText',
            fontWeight: 700,
          }}
        >
          {isLoading ? 'در حال ارسال...' : 'تایید'}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default VerifyIdentification;
